// AiFieldHelpers.js
// Pure helpers for AI-backed field display values.

import { buildOptionLookup } from "./FieldOptionHelpers.js";
import {
  AiComparisonStatus,
  DEFAULT_AI_COMPARISON_TONE,
} from "../Enums.js";

const AI_SUGGESTED_DETAIL_KEY_PRIORITY = [
  "display",
  "strength",
  "recommendation",
  "topic",
  "speciality",
  "specialty",
  "sig",
  "submissionType",
  "combined",
];

function getNestedPropertyValue(object, path) {
  if (!object || typeof object !== "object" || !path || typeof path !== "string") return undefined;
  return path
    .split(".")
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), object);
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function normalizeComparableValue(value) {
  if (Array.isArray(value)) {
    return value
      .filter((item) => !isEmptyValue(item))
      .map((item) => String(item))
      .sort()
      .join("|");
  }

  return isEmptyValue(value) ? "" : String(value);
}

function valueFromAiItem(item) {
  if (item == null) return null;
  if (typeof item !== "object") return item;
  return item.value ?? item.name ?? item.label ?? null;
}

function normalizeAiCandidateItems(rawCandidates) {
  const candidates = Array.isArray(rawCandidates) ? rawCandidates : [];

  return candidates
    .map((candidate) => {
      const value = valueFromAiItem(candidate);
      if (isEmptyValue(value)) return null;

      return {
        value,
        name:
          candidate && typeof candidate === "object"
            ? candidate.name ?? candidate.label ?? null
            : null,
        score:
          candidate && typeof candidate === "object" && typeof candidate.score === "number"
            ? candidate.score
            : null,
        raw: candidate,
      };
    })
    .filter(Boolean);
}

function labelValue(value, optionLookup) {
  if (isEmptyValue(value)) return "";
  return optionLookup.labelForValue(value) || formatAiSuggestedName(value);
}

export function formatAiSuggestedName(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  if (typeof item !== "object") return "";
  if (typeof item.name === "string" && item.name.trim()) return item.name.trim();
  if (typeof item.label === "string" && item.label.trim()) return item.label.trim();
  if (typeof item.value === "string" && item.value.trim()) {
    return item.value
      .trim()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return "";
}

export function normalizeClassificationEntry(entry) {
  if (!entry || typeof entry !== "object") return null;

  const score = typeof entry.score === "number" ? entry.score : null;
  const name = formatAiSuggestedName(entry);

  if (!name && !entry.value) return null;

  return {
    level: entry.level || null,
    name,
    value: entry.value || null,
    parentValue: entry.parentValue || null,
    score,
  };
}

export function normalizeClassificationGroup(group) {
  if (!group || typeof group !== "object") {
    return {
      recommendation: null,
      matches: [],
      ambiguity: null,
    };
  }

  const matches = Array.isArray(group.matches)
    ? group.matches.map(normalizeClassificationEntry).filter(Boolean)
    : [];

  const recommendation = group.recommendation
    ? normalizeClassificationEntry(group.recommendation)
    : null;

  const ambiguity =
    group.ambiguity && typeof group.ambiguity === "object" ? group.ambiguity : null;

  return {
    recommendation,
    matches,
    ambiguity,
  };
}

export function getAiSuggestedRawValues(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "object") return [value];

  if (value?.ambiguity?.detected) {
    const candidates = Array.isArray(value?.ambiguity?.candidates) ? value.ambiguity.candidates : [];
    return candidates
      .map((candidate) => candidate?.value ?? candidate?.name ?? null)
      .filter((candidate) => candidate !== null && candidate !== undefined && candidate !== "");
  }

  if (value?.recommendation) {
    return [value.recommendation.value ?? value.recommendation.name ?? value.recommendation];
  }

  if (Array.isArray(value?.matches) && value.matches.length > 0) {
    return value.matches
      .map((match) => match?.value ?? match?.name ?? match)
      .filter((match) => match !== null && match !== undefined && match !== "");
  }

  return [value];
}

export function getAiSuggestedConfiguredValue(value, field) {
  if (value == null || !field?.details || typeof field.details !== "object") return value;

  const detailEntries = Object.entries(field.details).filter(([key, config]) => {
    if (key === "tooltip" || key === "type") return false;
    return !!config && typeof config === "object";
  });

  if (detailEntries.length === 0) return value;

  const sortedEntries = [...detailEntries].sort(([aKey, aConfig], [bKey, bConfig]) => {
    const aHasRange = aConfig?.range ? 1 : 0;
    const bHasRange = bConfig?.range ? 1 : 0;
    if (aHasRange !== bHasRange) return bHasRange - aHasRange;

    const aPriority = AI_SUGGESTED_DETAIL_KEY_PRIORITY.indexOf(aKey);
    const bPriority = AI_SUGGESTED_DETAIL_KEY_PRIORITY.indexOf(bKey);
    const aRank = aPriority === -1 ? Number.MAX_SAFE_INTEGER : aPriority;
    const bRank = bPriority === -1 ? Number.MAX_SAFE_INTEGER : bPriority;

    return aRank - bRank;
  });

  for (const [detailKey, config] of sortedEntries) {
    const path = config?.dataSource || detailKey;
    const candidate = getNestedPropertyValue(value, path);
    if (candidate !== undefined && candidate !== null && candidate !== "") {
      return candidate;
    }
  }

  return value;
}

export function getAiSuggestedDisplayValue(value, field, targetOptions = {}) {
  if (value == null) return "";

  const configuredValue = getAiSuggestedConfiguredValue(value, field);
  const optionLookup = buildOptionLookup(targetOptions);

  const labels = getAiSuggestedRawValues(configuredValue)
    .map((item) => {
      if (item && typeof item === "object") {
        const rawKey = item?.value ?? item?.name ?? "";
        const mapped = rawKey !== "" ? optionLookup.labelForValue(rawKey) : "";
        return mapped || formatAiSuggestedName(item);
      }

      const rawKey = String(item);
      return optionLookup.labelForValue(rawKey) || formatAiSuggestedName(item);
    })
    .filter(Boolean);

  if (labels.length === 0 && configuredValue?.ambiguity?.detected) {
    return "Ambiguous";
  }

  return labels.join(", ");
}

export function getAiSuggestedMeta(value, field, targetOptions = {}) {
  const displayValue = getAiSuggestedDisplayValue(value, field, targetOptions);
  const isAmbiguous = !!(value && typeof value === "object" && value?.ambiguity?.detected);
  const candidateCount =
    value && typeof value === "object" && Array.isArray(value?.ambiguity?.candidates)
      ? value.ambiguity.candidates.length
      : 0;

  return {
    displayValue,
    isAmbiguous,
    candidateCount,
  };
}

export function buildAiComparisonState({
  row,
  field,
  submitterValue,
  targetOptions = {},
  projectConfig = null,
} = {}) {
  const config = field?.attributes?.aiComparison || {};
  const labels = config.labels || {};
  const toneConfig = {
    ...DEFAULT_AI_COMPARISON_TONE,
    ...(projectConfig?.aiComparison?.tone || {}),
    ...(config.tone || {}),
  };
  const optionLookup = buildOptionLookup(targetOptions);

  if (config.enabled !== true) {
    return {
      enabled: false,
      status: AiComparisonStatus.DISABLED,
      tone: toneConfig[AiComparisonStatus.DISABLED],
      labels,
      display: config.display || {},
    };
  }

  const aiSource = getNestedPropertyValue(row, config.aiDataSource);
  const aiRecommendation = config.aiRecommendationPath
    ? getNestedPropertyValue(aiSource, config.aiRecommendationPath)
    : valueFromAiItem(aiSource);
  const aiCandidatesRaw = config.aiCandidatesPath
    ? getNestedPropertyValue(aiSource, config.aiCandidatesPath)
    : [];
  const aiAmbiguity = config.aiAmbiguityPath
    ? getNestedPropertyValue(aiSource, config.aiAmbiguityPath)
    : null;

  const aiCandidates = normalizeAiCandidateItems(aiCandidatesRaw);
  const submitterHasValue = !isEmptyValue(submitterValue);
  const aiHasRecommendation = !isEmptyValue(aiRecommendation);
  const normalizedSubmitterValue = normalizeComparableValue(submitterValue);
  const submitterMatchesAnyCandidate =
    submitterHasValue &&
    aiCandidates.some(
      (candidate) =>
        normalizeComparableValue(candidate?.value ?? candidate?.raw) === normalizedSubmitterValue
    );
  const isAmbiguous =
    aiAmbiguity?.detected === true ||
    (!aiHasRecommendation && aiCandidates.length > 1);

  let status = AiComparisonStatus.NO_AI_PICK;
  if (isAmbiguous) {
    status = AiComparisonStatus.AMBIGUOUS;
  } else if (submitterHasValue && aiHasRecommendation) {
    status =
      normalizeComparableValue(submitterValue) === normalizeComparableValue(aiRecommendation)
        ? AiComparisonStatus.SAME
        : AiComparisonStatus.DIFFERENT;
  } else if (!submitterHasValue && aiHasRecommendation) {
    status = AiComparisonStatus.SUGGESTION;
  } else {
    status = AiComparisonStatus.NO_AI_PICK;
  }

  const candidateLabels = aiCandidates.map((candidate) => ({
    ...candidate,
    label: optionLookup.labelForValue(candidate.value) || formatAiSuggestedName(candidate.raw),
  }));

  return {
    enabled: true,
    status,
    tone: toneConfig[status],
    labels,
    display: config.display || {},
    submitterValue,
    submitterLabel: labelValue(submitterValue, optionLookup),
    aiValue: aiRecommendation,
    aiLabel: labelValue(aiRecommendation, optionLookup),
    aiCandidates: candidateLabels,
    aiAmbiguity,
    submitterMatchesAnyCandidate,
    raw: {
      aiSource,
      aiRecommendation,
      aiCandidates: aiCandidatesRaw,
      aiAmbiguity,
    },
  };
}
